import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import { PlanRun, TaskPlan } from "../types";
import { getDashboardData, getRuns } from "../services/storageService";
import { useWorkspace } from "./WorkspaceProvider";

interface UseWorkspaceDataOptions {
  autoFetch?: boolean;
}

export const useWorkspaceData = (
  options: UseWorkspaceDataOptions = { autoFetch: true },
) => {
  const { currentOrg, refreshOrgs } = useWorkspace();
  const { getToken } = useAuth();

  const [plans, setPlans] = useState<TaskPlan[]>([]);
  const [runs, setRuns] = useState<PlanRun[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(
    async (fetchOptions?: {
      dashboard?: boolean;
      runsOnly?: boolean;
      skipOrgs?: boolean;
    }) => {
      if (!currentOrg) return;

      setIsLoading(true);
      setError(null);
      try {
        const token = await getToken();
        const isDashboard = fetchOptions?.dashboard ?? true;
        const runsOnly = fetchOptions?.runsOnly ?? false;
        const skipOrgs = fetchOptions?.skipOrgs ?? false;

        const promises: Promise<any>[] = [];
        if (isDashboard) {
          promises.push(getDashboardData(currentOrg.id, token || undefined));
        } else if (runsOnly) {
          promises.push(getRuns(currentOrg.id, token || undefined));
        } else {
          promises.push(getDashboardData(currentOrg.id, token || undefined));
        }

        if (!skipOrgs) {
          promises.push(refreshOrgs());
        }

        const results = await Promise.all(promises);

        if (isDashboard || (!isDashboard && !runsOnly)) {
          setPlans(results[0].plans);
          setRuns(results[0].runs);
        } else if (runsOnly) {
          setRuns(results[0]);
        }
      } catch (e) {
        console.error("Failed to fetch workspace data:", e);
        setError("Failed to load workspace data.");
      } finally {
        setIsLoading(false);
      }
    },
    [currentOrg, getToken, refreshOrgs],
  );

  useEffect(() => {
    if (options.autoFetch && currentOrg) {
      fetchData({ dashboard: true, skipOrgs: true });
    }
  }, [currentOrg?.id, options.autoFetch]);

  return {
    plans,
    runs,
    isLoading,
    error,
    refresh: fetchData,
    setPlans,
    setRuns,
  };
};
